import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Phone, Mailbox, ExternalLink, Smartphone, Shield } from 'lucide-react';
export function SpamGuidePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen w-full bg-white font-sans text-[#1a0647]" data-id="element-2311">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-2312">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-2313">
          <div className="flex items-center gap-5" data-id="element-2314">
            <Link to="/home" data-id="element-2315">
              <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo" className="h-7 w-auto" data-id="element-2316" />
            </Link>
            <Link to="/resources" className="text-gray-400 hover:text-gray-600 text-sm transition-colors hidden sm:inline-flex items-center gap-1" data-id="element-2317">
              ← Back to Resources
            </Link>
          </div>

          <div className="flex items-center gap-3" data-id="element-2318">
            <a href="/login" className="text-[#111111] no-underline hover:text-[#631bb7] px-3 py-1.5 rounded-lg font-medium text-sm transition-colors" data-id="element-2319">
              Login
            </a>
            <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-4 py-2 rounded-lg font-semibold text-sm no-underline hover:bg-[#521099] transition-colors shadow-sm" data-id="element-2320">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#1a0647] text-white py-10 md:py-12 px-4" data-id="element-2321">
        <div className="max-w-5xl mx-auto" data-id="element-2322">
          <div className="flex flex-row items-center gap-4" data-id="element-2323">
            <div className="flex-1" data-id="element-2324">
              <h1 className="font-bold text-2xl md:text-3xl lg:text-4xl leading-tight text-white" data-id="element-2325">
                Reducing Spam and Junk Mail
              </h1>
              <p className="text-gray-300 mt-3 text-base md:text-lg max-w-2xl" data-id="element-2326">
                Spam doesn't stop on its own. Here's how to push back across
                every channel, using tools you already have on your phone or
                computer.
              </p>
            </div>
            <div className="flex-shrink-0 w-20 md:w-28" data-id="element-2327">
              <div className="w-full aspect-square bg-[#8528e2]/20 rounded-2xl flex items-center justify-center" data-id="element-2328">
                <Shield size={48} className="text-[#b063ff]" data-id="element-2329" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-[#f8f8f8] py-6 px-4 border-b border-gray-200" data-id="element-2330">
        <div className="max-w-5xl mx-auto" data-id="element-2331">
          <div className="flex flex-wrap justify-center gap-3" data-id="element-2332">
            <a href="#email" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-2333">
              <Mail size={16} data-id="element-2334" />
              Email
            </a>
            <a href="#text" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-2335">
              <MessageSquare size={16} data-id="element-2336" />
              Text Messages
            </a>
            <a href="#phone" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-2337">
              <Phone size={16} data-id="element-2338" />
              Phone Calls
            </a>
            <a href="#mail" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-[#1a0647] hover:border-[#8528e2] hover:text-[#8528e2] transition-colors no-underline" data-id="element-2339">
              <Mailbox size={16} data-id="element-2340" />
              Physical Mail
            </a>
          </div>
        </div>
      </section>

      {/* EMAIL Section */}
      <section id="email" className="py-16 md:py-20 px-4 max-w-5xl mx-auto" data-id="element-2341">
        <div className="flex items-center gap-4 mb-8" data-id="element-2342">
          <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-2343">
            <Mail size={28} className="text-white" data-id="element-2344" />
          </div>
          <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-2345">
            Email
          </h2>
        </div>

        <p className="text-lg text-gray-700 leading-relaxed mb-10 max-w-3xl" data-id="element-2346">
          Your email provider has built-in tools to report suspicious messages,
          and using them actually makes a difference. Every report you submit
          helps train the filter to catch more junk in the future.
        </p>

        <div className="space-y-6" data-id="element-2347">
          {/* Gmail Desktop/Android */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-2348">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2349">
              Gmail (on a computer or Android)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-2350">
              <li data-id="element-2351">Open the suspicious email.</li>
              <li data-id="element-2352">Click the three vertical dots next to the Reply button.</li>
              <li data-id="element-2353">
                Select <strong data-id="element-2354">"Report phishing"</strong> if it looks like a
                scam trying to steal your information, or{' '}
                <strong data-id="element-2355">"Report spam"</strong> if it's just junk you didn't ask
                for.
              </li>
              <li data-id="element-2356">
                The email disappears from your inbox and gets flagged for
                review.
              </li>
            </ol>
          </div>

          {/* Gmail iPhone */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-2357">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2358">
              Gmail (on iPhone)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-2359">
              <li data-id="element-2360">Open the email.</li>
              <li data-id="element-2361">Tap the three dots at the top right of the message.</li>
              <li data-id="element-2362">
                Tap <strong data-id="element-2363">"Report spam."</strong>
              </li>
            </ol>
            <p className="text-sm text-gray-500 mt-3 italic" data-id="element-2364">
              There is no separate "Report phishing" option in the iPhone app,
              so this is the right button for both situations.
            </p>
          </div>

          {/* Outlook */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-2365">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2366">
              Outlook (on a computer)
            </h3>
            <ol className="list-decimal pl-5 text-gray-700 space-y-2" data-id="element-2367">
              <li data-id="element-2368">
                Select the suspicious message and click the{' '}
                <strong data-id="element-2369">"Report"</strong> button in the toolbar at the top of
                the screen.
              </li>
              <li data-id="element-2370">
                Choose <strong data-id="element-2371">"Report phishing"</strong> or{' '}
                <strong data-id="element-2372">"Report junk."</strong>
              </li>
            </ol>
            <p className="text-sm text-gray-500 mt-3" data-id="element-2373">
              Microsoft uses your reports to improve filtering for everyone.
            </p>
          </div>

          {/* Apple Mail */}
          <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-6 rounded-r-lg" data-id="element-2374">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2375">
              Apple Mail (on a Mac or iPhone)
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-2376">
              Apple Mail does not have a dedicated "Report phishing" button.
              Your best option is to right-click the email and select{' '}
              <strong data-id="element-2377">"Move to Junk,"</strong> which tells Apple's filters to
              treat that sender as unwanted going forward.
            </p>
            <p className="text-gray-700" data-id="element-2378">
              For anything that looks like a real scam, forward it to{' '}
              <a href="mailto:reportphishing@apple.com" className="text-[#8528e2] font-medium hover:underline" data-id="element-2379">
                reportphishing@apple.com
              </a>
              .
            </p>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="mt-10 space-y-6" data-id="element-2380">
          <div className="bg-[#eae6dc] border-t-4 border-[#8528e2] p-6 rounded-lg" data-id="element-2381">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2382">
              💡 Want to fight back more actively?
            </h3>
            <p className="text-gray-700" data-id="element-2383">
              Forward scam emails to Re:Scam at{' '}
              <a href="mailto:me@rescam.org" className="text-[#8528e2] font-medium hover:underline" data-id="element-2384">
                me@rescam.org
              </a>
              . Their AI will reply to the scammer on your behalf and keep them
              tied up in a fake conversation, wasting their time so they have
              less of it to spend on real targets.
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-2385">
            <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2386">
              Opt out of marketing email lists
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-2387">
              You can also opt out of marketing email lists at:
            </p>
            <a href="https://www.ims-dm.com/cgi/offemaillist.php" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#8528e2] font-medium hover:underline" data-id="element-2388">
              ims-dm.com/cgi/offemaillist.php
              <ExternalLink size={14} data-id="element-2389" />
            </a>
          </div>
        </div>
      </section>

      {/* TEXT MESSAGES Section */}
      <section id="text" className="py-16 md:py-20 px-4 bg-[#f8f8f8] border-t border-gray-200" data-id="element-2390">
        <div className="max-w-5xl mx-auto" data-id="element-2391">
          <div className="flex items-center gap-4 mb-8" data-id="element-2392">
            <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-2393">
              <MessageSquare size={28} className="text-white" data-id="element-2394" />
            </div>
            <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-2395">
              Text Messages
            </h2>
          </div>

          <div className="space-y-8" data-id="element-2396">
            {/* Report to Carrier */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-2397">
              <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-2398">
                Report to your carrier
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2399">
                The fastest way to report a spam text is to forward it to{' '}
                <strong className="text-[#8528e2]" data-id="element-2400">7726</strong>. That number
                spells "SPAM" on your keypad. All three major carriers (AT&T,
                T-Mobile, and Verizon) use this number.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-2401">
                After you forward the message, you'll get a reply asking for the
                sender's phone number. Just reply with the number that texted
                you.
              </p>
              <div className="bg-[#eae6dc] p-4 rounded-lg" data-id="element-2402">
                <p className="text-[#1a0647] font-medium" data-id="element-2403">
                  <strong data-id="element-2404">Pro tip:</strong> Save 7726 in your contacts as
                  "Report SPAM" so you can find it quickly when you need it.
                </p>
              </div>
            </div>

            {/* iPhone */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-2405">
              <div className="flex items-center gap-3 mb-4" data-id="element-2406">
                <Smartphone size={24} className="text-[#8528e2]" data-id="element-2407" />
                <h3 className="font-bold text-xl text-[#1a0647]" data-id="element-2408">
                  iPhone users: built-in reporting
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2409">
                If you receive a text from a number that is not in your
                contacts, a <strong data-id="element-2410">"Report Junk"</strong> link will appear at
                the bottom of the message. Tap it, then tap{' '}
                <strong data-id="element-2411">"Delete and Report Junk."</strong> This reports the
                sender to Apple and your carrier at the same time.
              </p>
              <p className="text-sm text-gray-500 italic mb-6" data-id="element-2412">
                Note: if you have already replied to the message, this option
                will no longer appear.
              </p>

              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg" data-id="element-2413">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2414">
                  Filter unknown senders
                </h4>
                <p className="text-gray-700 text-sm mb-3" data-id="element-2415">
                  You can turn on filtering so texts from unknown numbers are
                  silently sorted into a separate folder:
                </p>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-2416">
                  <li data-id="element-2417">
                    Go to <strong data-id="element-2418">Settings</strong>
                  </li>
                  <li data-id="element-2419">
                    Tap <strong data-id="element-2420">Apps</strong>, then tap{' '}
                    <strong data-id="element-2421">Messages</strong>
                  </li>
                  <li data-id="element-2422">
                    Turn on <strong data-id="element-2423">"Filter Unknown Senders"</strong>
                  </li>
                </ol>
                <p className="text-gray-600 text-sm mt-3" data-id="element-2424">
                  Texts from numbers you don't know will land in a separate
                  folder labeled "Unknown Senders," and you won't get a
                  notification.
                </p>
              </div>
            </div>

            {/* Android */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-2425">
              <div className="flex items-center gap-3 mb-4" data-id="element-2426">
                <Smartphone size={24} className="text-[#8528e2]" data-id="element-2427" />
                <h3 className="font-bold text-xl text-[#1a0647]" data-id="element-2428">
                  Android users: built-in reporting and filtering
                </h3>
              </div>

              <p className="text-gray-700 mb-4" data-id="element-2429">
                If you use the Google Messages app (the default on most Android
                phones), spam protection is usually on by default. To confirm
                it's active:
              </p>
              <ol className="list-decimal pl-5 text-gray-700 mb-6 space-y-1" data-id="element-2430">
                <li data-id="element-2431">Tap your profile picture or initials in the top right</li>
                <li data-id="element-2432">
                  Go to <strong data-id="element-2433">"Messages settings"</strong>
                </li>
                <li data-id="element-2434">
                  Then <strong data-id="element-2435">"Spam protection"</strong>
                </li>
                <li data-id="element-2436">
                  Make sure <strong data-id="element-2437">"Enable spam protection"</strong> is toggled
                  on
                </li>
              </ol>

              <div className="bg-[#f8f8f8] border-l-4 border-[#01ced1] p-4 rounded-r-lg mb-4" data-id="element-2438">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2439">
                  To report a spam text:
                </h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-2440">
                  <li data-id="element-2441">Press and hold the conversation in your message list</li>
                  <li data-id="element-2442">
                    Tap <strong data-id="element-2443">"Block"</strong>
                  </li>
                  <li data-id="element-2444">
                    Tap <strong data-id="element-2445">"Report spam"</strong>
                  </li>
                  <li data-id="element-2446">
                    Tap <strong data-id="element-2447">"OK"</strong>
                  </li>
                </ol>
                <p className="text-gray-600 text-sm mt-2" data-id="element-2448">
                  This blocks the sender, moves the message to your spam folder,
                  and reports the number to Google.
                </p>
              </div>

              <p className="text-gray-700 text-sm" data-id="element-2449">
                <strong data-id="element-2450">Samsung phone?</strong> Open the Samsung Messages app,
                tap the three dots, go to "Settings," then "Block numbers and
                spam," and toggle on "Filter spam messages."
              </p>
            </div>

            {/* FTC */}
            <div className="bg-white border border-gray-200 p-6 rounded-lg" data-id="element-2451">
              <h3 className="font-bold text-lg text-[#1a0647] mb-3" data-id="element-2452">
                Report to the FTC
              </h3>
              <p className="text-gray-700 mb-3" data-id="element-2453">
                You can also report spam texts (and scam calls) at{' '}
                <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer" className="text-[#8528e2] font-medium hover:underline" data-id="element-2454">
                  ReportFraud.ftc.gov
                </a>
                . The FTC uses these reports to investigate the biggest
                offenders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHONE CALLS Section */}
      <section id="phone" className="py-16 md:py-20 px-4 max-w-5xl mx-auto" data-id="element-2455">
        <div className="flex items-center gap-4 mb-8" data-id="element-2456">
          <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-2457">
            <Phone size={28} className="text-white" data-id="element-2458" />
          </div>
          <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-2459">
            Phone Calls
          </h2>
        </div>

        <div className="space-y-8" data-id="element-2460">
          {/* Do Not Call Registry */}
          <div className="bg-[#eae6dc] p-6 rounded-xl" data-id="element-2461">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-2462">
              Register with the Do Not Call Registry
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-2463">
              Start here if you haven't already. Visit{' '}
              <a href="https://www.donotcall.gov" target="_blank" rel="noopener noreferrer" className="text-[#8528e2] font-medium hover:underline" data-id="element-2464">
                DoNotCall.gov
              </a>{' '}
              or call <strong className="text-[#1a0647]" data-id="element-2465">1-888-382-1222</strong>{' '}
              (TTY: 1-866-290-4236) from the phone number you want to register.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-2466">
              If you register online, you'll receive a confirmation email with a
              link you'll need to click within 72 hours to complete your
              registration. You must call from, or register, the exact number
              you want protected.
            </p>
            <p className="text-gray-600 text-sm italic" data-id="element-2467">
              Legitimate telemarketers are required to honor this list. Scammers
              won't, which is why the tools below matter just as much.
            </p>
          </div>

          {/* Carrier Tools */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-2468">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-2469">
              Turn on your carrier's free spam blocking tool
            </h3>
            <p className="text-gray-700 mb-6" data-id="element-2470">
              Your phone company almost certainly offers a free app or service
              that blocks scam calls before they even ring. Here's what each
              major carrier offers:
            </p>

            <div className="space-y-4" data-id="element-2471">
              {/* T-Mobile */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#e20074] p-5 rounded-r-lg" data-id="element-2472">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2473">
                  T-Mobile (including Metro by T-Mobile)
                </h4>
                <p className="text-gray-700 text-sm mb-2" data-id="element-2474">
                  <strong data-id="element-2475">Scam ID</strong> is already enabled on your account by
                  default. When a suspicious call comes in, it will show "Scam
                  Likely" on your screen so you know not to answer.
                </p>
                <p className="text-gray-700 text-sm mb-2" data-id="element-2476">
                  To take it a step further, you can turn on{' '}
                  <strong data-id="element-2477">Scam Block</strong>, which automatically prevents
                  those calls from going through at all.
                </p>
                <p className="text-[#1a0647] font-medium text-sm" data-id="element-2478">
                  Dial <strong data-id="element-2479">#662#</strong> from your T-Mobile phone to turn
                  it on. You can also download the T-Life app and manage it from
                  there.
                </p>
              </div>

              {/* AT&T */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#009fdb] p-5 rounded-r-lg" data-id="element-2480">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2481">AT&T</h4>
                <p className="text-gray-700 text-sm" data-id="element-2482">
                  Download the free <strong data-id="element-2483">AT&T ActiveArmor app</strong> from
                  the App Store or Google Play. Once installed, it automatically
                  blocks fraud calls at the network level, flags suspected spam,
                  and lets you send unknown callers straight to voicemail. No
                  ongoing setup required after the initial download.
                </p>
              </div>

              {/* Verizon */}
              <div className="bg-[#f8f8f8] border-l-4 border-[#cd040b] p-5 rounded-r-lg" data-id="element-2484">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2485">Verizon</h4>
                <p className="text-gray-700 text-sm" data-id="element-2486">
                  Download the free <strong data-id="element-2487">Verizon Call Filter app</strong>{' '}
                  from the App Store or Google Play. It identifies incoming spam
                  calls, alerts you when one is suspected, and lets you report
                  numbers directly from the app. A paid upgrade (Call Filter
                  Plus) adds caller ID and additional features, but the free
                  version is a solid starting point.
                </p>
              </div>
            </div>
          </div>

          {/* Block Numbers */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-2488">
            <h3 className="font-bold text-xl text-[#1a0647] mb-4" data-id="element-2489">
              How to block numbers on your phone
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-2490">
              If you receive a specific number you want to block permanently,
              here's how to do it by device:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2491">
              <div className="bg-[#f8f8f8] p-4 rounded-lg" data-id="element-2492">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2493">On iPhone</h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-2494">
                  <li data-id="element-2495">Open the Phone app</li>
                  <li data-id="element-2496">Tap "Recents"</li>
                  <li data-id="element-2497">Find the number</li>
                  <li data-id="element-2498">Tap the small "i" icon next to it</li>
                  <li data-id="element-2499">Scroll down</li>
                  <li data-id="element-2500">
                    Tap <strong data-id="element-2501">"Block this Caller"</strong>
                  </li>
                </ol>
              </div>

              <div className="bg-[#f8f8f8] p-4 rounded-lg" data-id="element-2502">
                <h4 className="font-bold text-[#1a0647] mb-2" data-id="element-2503">
                  On Android (Google Phone app)
                </h4>
                <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-1" data-id="element-2504">
                  <li data-id="element-2505">Open the Phone app</li>
                  <li data-id="element-2506">Tap "Recents"</li>
                  <li data-id="element-2507">Tap the number</li>
                  <li data-id="element-2508">
                    Tap <strong data-id="element-2509">"Block/report spam"</strong>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHYSICAL MAIL Section */}
      <section id="mail" className="py-16 md:py-20 px-4 bg-[#f8f8f8] border-t border-gray-200" data-id="element-2510">
        <div className="max-w-5xl mx-auto" data-id="element-2511">
          <div className="flex items-center gap-4 mb-8" data-id="element-2512">
            <div className="w-14 h-14 bg-[#8528e2] rounded-2xl flex items-center justify-center" data-id="element-2513">
              <Mailbox size={28} className="text-white" data-id="element-2514" />
            </div>
            <h2 className="font-bold text-2xl md:text-3xl text-[#1a0647]" data-id="element-2515">
              Physical Mail
            </h2>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm max-w-3xl" data-id="element-2516">
            <p className="text-gray-700 text-lg mb-6" data-id="element-2517">
              To reduce unsolicited catalogs, credit card offers, and marketing
              mailers, register with <strong data-id="element-2518">DMAchoice</strong>, a mail
              preference service run by the Data and Marketing Association.
            </p>
            <p className="text-gray-700 mb-6" data-id="element-2519">
              You can manage your preferences at:
            </p>
            <a href="https://www.dmachoice.org/static/consumer_choice_tools.php" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#8528e2] hover:bg-[#b063ff] text-white px-6 py-3 rounded-lg font-bold transition-colors" data-id="element-2520">
              Visit DMAchoice
              <ExternalLink size={16} data-id="element-2521" />
            </a>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="bg-[#1a0647] text-white py-16 md:py-20 px-4" data-id="element-2522">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2523">
          <div className="flex justify-center mb-8" data-id="element-2524">
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Icon-Solo-FC.png" alt="Caveo Icon" className="h-16 w-auto" data-id="element-2525" />
          </div>
          <h2 className="font-bold text-3xl mb-6" data-id="element-2526">A Note from Caveo</h2>
          <div className="text-lg text-gray-300 leading-relaxed space-y-6 mb-10 max-w-3xl mx-auto" data-id="element-2527">
            <p data-id="element-2528">
              Spam is more than annoying — it's often the first step in a scam.
              By taking a few minutes to report, block, and opt out, you're not
              just cleaning up your inbox. You're making it harder for bad
              actors to reach you and your family.
            </p>
            <p data-id="element-2529">
              Every report you submit helps train filters to catch more junk.
              Every number you block is one less interruption. Small actions add
              up.
            </p>
          </div>
          <Link to="/intro-assessment" className="inline-block bg-[#8528e2] hover:bg-[#b063ff] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg no-underline" data-id="element-2530">
            Get Your Free Security Checkup
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#110430] text-gray-400 py-12 px-4 border-t border-white/10" data-id="element-2531">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left" data-id="element-2532">
          <div className="flex items-center gap-4" data-id="element-2533">
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Icon-Solo-FC.png" alt="Caveo" className="h-8 w-auto opacity-80" data-id="element-2534" />
            <span className="text-xs" data-id="element-2535">
              © 2026 AppEmbrace Inc. DBA Caveo. All rights reserved.
            </span>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden z-50 transition-transform duration-300 ${isScrolled ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-2536">
        <Link to="/intro-assessment" className="block w-full bg-[#8528e2] text-white py-3 rounded-lg font-bold text-center shadow-lg no-underline" data-id="element-2537">
          Get Your Free Security Checkup →
        </Link>
      </div>
    </div>;
}